import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { degreeMetrics } from "@/lib/compareDegreeMetrics";
import { useCurrency } from "@/context/CurrencyContext";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { X, Search, ExternalLink, ChevronRight, ArrowRight, RotateCcw, Share2, Printer, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import StudentLovedBadge from "@/components/app/StudentLovedBadge";
import ProfessorVerdictCard from "@/components/app/ProfessorVerdictCard";
import PlusReportCompactStrip from "@/components/app/PlusReportCompactStrip";
import CompareChipBar from "@/components/app/CompareChipBar";
import PlacementsHintChip from "@/components/app/PlacementsHintChip";
import { useStudentLovedBulk } from "@/lib/useStudentLoved";
import NssTrendSparkline from "@/components/app/NssTrendSparkline";
import StudentExperienceTrendSparkline from "@/components/app/StudentExperienceTrendSparkline";
// ============================================================================
// University comparison rows (kept from prior version)
// ============================================================================
const UNI_ROWS = [
  { key: "world_rank", label: "World rank", fmt: (v) => `#${v}` },
  { key: "acceptance_rate", label: "Acceptance rate", fmt: (v) => `${(v * 100).toFixed(1)}%` },
  { key: "international_pct", label: "International %", fmt: (v) => `${(v * 100).toFixed(0)}%` },
  { key: "tuition_intl_annual_usd", label: "Tuition (intl., /yr)", cur: true },
  { key: "accommodation_annual_usd", label: "Accommodation /yr", cur: true },
  { key: "food_annual_usd", label: "Food /yr", cur: true },
  { key: "avg_starting_salary_usd", label: "Grad. starting salary", cur: true },
  { key: "employment_rate", label: "Employment rate", fmt: (v) => `${(v * 100).toFixed(0)}%` },
  { key: "student_faculty_ratio", label: "Student:faculty", fmt: (v) => `${v}:1` },
  { key: "satisfaction_score", label: "Satisfaction", fmt: (v) => `${v}/5` },
  { key: "work_rights_after", label: "Post-study visa" },
];


// Numeric/currency rows: unknown, withheld or non-finite -> "—" (a real 0, e.g. free tuition, still shows). Text rows: blank -> "—".
function uniCell(row, v, format) {
  if (row.cur || row.fmt) return typeof v === "number" && Number.isFinite(v) ? (row.cur ? format(v) : row.fmt(v)) : "—";
  return v == null || v === "" ? "—" : v;
}

const PICK_PAGE = 200;

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// University picker options from the server: per-country counts for the (debounced) search, names per country/search.
function useUniOptions(query) {
  const q = useDebounced(query.trim());
  const [countries, setCountries] = useState(null);
  const [lists, setLists] = useState({});
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const seq = useRef(0);
  const qRef = useRef(q);
  qRef.current = q;
  useEffect(() => {
    const mine = ++seq.current;
    setCountries(null);
    setError(false);
    api.get("/universities/compare-options", { params: { q, limit: q ? PICK_PAGE : 0 } })
      .then((r) => {
        if (mine !== seq.current) return;
        setCountries(r.data.countries);
        if (q) setLists((m) => ({ ...m, [`${q}\n*`]: r.data.items }));
      })
      .catch(() => { if (mine === seq.current) setError(true); });
  }, [q, tick]);
  const fetchCountry = (country, offset) => {
    const forQ = qRef.current;
    const key = `${forQ}\n${country}`;
    api.get("/universities/compare-options", { params: { q: forQ, country, limit: PICK_PAGE, offset } })
      .then((r) => setLists((m) => ({ ...m, [key]: offset ? [...(m[key] || []), ...r.data.items] : r.data.items })))
      .catch(() => setError(true));
  };
  const listFor = (country) => {
    const own = lists[`${q}\n${country}`];
    if (own) return own;
    const all = q ? lists[`${q}\n*`] : null;
    return all ? all.filter((u) => (u.country || "Other") === country) : null;
  };
  return {
    q, countries, listFor, error,
    ensureCountry: (country) => { if (!q && !lists[`\n${country}`]) fetchCountry(country, 0); },
    loadMore: (country) => fetchCountry(country, (lists[`${q}\n${country}`] || []).length),
    retry: () => setTick((n) => n + 1),
  };
}

// Full records for the selected ids only (requested id -> record, or null when it no longer exists).
function useUniDetails(ids) {
  const [docs, setDocs] = useState({});
  const asked = useRef(new Set());
  const key = ids.join(",");
  useEffect(() => {
    ids.filter((id) => !asked.current.has(id)).forEach((id) => {
      asked.current.add(id);
      api.get(`/universities/${encodeURIComponent(id)}`)
        .then((r) => setDocs((m) => ({ ...m, [id]: r.data })))
        .catch(() => setDocs((m) => ({ ...m, [id]: null })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return docs;
}

// Degree typeahead: first 8 server matches for the debounced query, already-selected ids excluded.
function useDegreeOptions(query, exclude) {
  const q = useDebounced(query.trim());
  const [state, setState] = useState({ q: "", items: [], error: false });
  const seq = useRef(0);
  const ex = exclude.join(",");
  useEffect(() => {
    const mine = ++seq.current;
    if (!q) { setState({ q: "", items: [], error: false }); return; }
    api.get("/degrees/compare-options", { params: { q, exclude: ex, limit: 8 } })
      .then((r) => { if (mine === seq.current) setState({ q, items: r.data.items, error: false }); })
      .catch(() => { if (mine === seq.current) setState({ q, items: [], error: true }); });
  }, [q, ex]);
  const live = query.trim();
  return { items: live ? state.items : [], settled: !!live && state.q === live, error: state.error };
}

// ============================================================================
// Universities tab
// ============================================================================
function UniversityCompare() {
  const [params, setParams] = useSearchParams();
  // URL-persisted selection — same shape as DegreeCompare so any Compare
  // view is a fully shareable link. Cap 4; keys u1..u4.
  const selected = useMemo(
    () => ["u1", "u2", "u3", "u4"].map((k) => params.get(k)).filter(Boolean),
    [params],
  );
  const setSelected = (ids) => {
    const next = new URLSearchParams(params);
    ["u1", "u2", "u3", "u4"].forEach((k) => next.delete(k));
    ids.slice(0, 4).forEach((id, i) => next.set(`u${i + 1}`, id));
    setParams(next, { replace: true });
  };
  const [query, setQuery] = useState("");
  const [openCountries, setOpenCountries] = useState(() => new Set());
  const [comparing, setComparing] = useState(selected.length >= 2);
  const tableRef = useRef(null);
  const { format } = useCurrency();

  const picker = useUniOptions(query);
  const details = useUniDetails(selected);

  const toggle = (id) => {
    if (selected.includes(id)) setSelected(selected.filter((x) => x !== id));
    else if (selected.length < 4) setSelected([...selected, id]);
  };

  const toggleCountry = (country) => {
    setOpenCountries((s) => {
      const next = new Set(s);
      if (next.has(country)) next.delete(country); else next.add(country);
      return next;
    });
    if (!openCountries.has(country)) picker.ensureCountry(country);
  };

  // Columns: each selected id's own record (requested id kept so chips/removal work for merged redirects).
  const chosen = useMemo(
    () => selected.map((id) => (details[id] ? { ...details[id], _reqId: id } : null)).filter(Boolean),
    [selected, details],
  );

  // Countries (whole catalogue, most universities first) with their match counts; names load per country/search.
  const grouped = (picker.countries || []).map((c) => [c.country, picker.listFor(c.country), c.count]);

  // Selection count per country — surfaced in the accordion header so a
  // collapsed country still signals "you have 2 picked in here".
  const selectedByCountry = useMemo(() => {
    const m = new Map();
    for (const u of chosen) {
      const c = u.country || "Other";
      m.set(c, (m.get(c) || 0) + 1);
    }
    return m;
  }, [chosen]);

  // When the user searches, auto-expand any matching country so the
  // filter isn't hidden inside a closed accordion.
  const searching = picker.q.length > 0;
  const isOpen = (country) => searching || openCountries.has(country);

  const cap = 4;
  const atCap = selected.length >= cap;
  const canCompare = selected.length >= 2;

  const runCompare = () => {
    setComparing(true);
    setOpenCountries(new Set());  // fold everything
    // Give React one frame to render the table before scrolling.
    requestAnimationFrame(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const clearAll = () => {
    setSelected([]);
    setComparing(false);
  };

  return (
    <div>
      {/* --- Selection summary + search ---------------------------------- */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4">
        <div className="flex-1 flex flex-wrap items-center gap-2 min-h-[36px]">
          {chosen.length === 0 && (
            <span className="text-sm text-stone-500">
              Pick up to <span className="font-mono">{cap}</span> universities, then press{" "}
              <span className="font-underground-sc tracking-wider text-[11px] text-[#4C1D95]">Compare</span>.
            </span>
          )}
          {chosen.map((u) => (
            <button
              key={u._reqId}
              type="button"
              onClick={() => toggle(u._reqId)}
              data-testid={`compare-chip-${u.id}`}
              className="group inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] text-white
                         pl-3 pr-2 py-1 text-xs tracking-wide hover:bg-[#3B0764] transition-colors"
              title="Remove"
            >
              <span className="max-w-[16ch] truncate">{u.name}</span>
              <X className="h-3 w-3 opacity-80 group-hover:opacity-100" />
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none" />
          <Input
            data-testid="compare-uni-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search universities…"
            className="pl-8 h-9 bg-white"
          />
        </div>
      </div>

      {/* --- Grouped list (accordion) ------------------------------------ */}
      <div className="rounded-lg border border-stone-200 bg-white/60 divide-y divide-stone-200">
        {picker.error && (
          <div className="p-6 text-sm text-stone-500 text-center" data-testid="compare-uni-options-error">
            Couldn't load universities.{" "}
            <button type="button" className="underline" onClick={picker.retry} data-testid="compare-uni-options-retry">Try again</button>
          </div>
        )}
        {!picker.error && picker.countries === null && (
          <div className="p-6 text-sm text-stone-500 text-center" data-testid="compare-uni-options-loading">Loading universities…</div>
        )}
        {picker.countries?.length === 0 && (
          <div className="p-6 text-sm text-stone-500 text-center" data-testid="compare-uni-no-match">
            No universities match "{picker.q}".
          </div>
        )}
        {grouped.map(([country, list, count]) => {
          const open = isOpen(country);
          const nSelected = selectedByCountry.get(country) || 0;
          return (
            <div key={country}>
              <button
                type="button"
                onClick={() => toggleCountry(country)}
                data-testid={`compare-country-toggle-${country.replace(/\s+/g, "-")}`}
                aria-expanded={open}
                className="w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-3
                           hover:bg-stone-100/60 transition-colors text-left"
              >
                <div className="flex items-center gap-2.5">
                  <ChevronRight
                    className={`h-4 w-4 text-stone-400 transition-transform ${open ? "rotate-90" : ""}`}
                  />
                  <span className="label-uppercase text-stone-700">{country}</span>
                  {nSelected > 0 && (
                    <span
                      className="text-[10px] font-underground-sc tracking-wider bg-[#F5F0FF] border border-[#4C1D95]/30 rounded-full px-1.5 py-0.5 text-[#4C1D95]"
                      data-testid={`compare-country-selected-${country.replace(/\s+/g, "-")}`}
                    >
                      {nSelected} picked
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                  {count} {count === 1 ? "uni" : "unis"}
                </div>
              </button>
              {open && (
                <div className="px-4 sm:px-5 pb-4 pt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
                    {!list && <div className="text-sm text-stone-400 py-1">Loading…</div>}
                    {(list || []).map((u) => {
                      const isOn = selected.includes(u.id);
                      const disabled = !isOn && atCap;
                      return (
                        <label
                          key={u.id}
                          data-testid={`compare-pick-${u.id}`}
                          className={`flex items-center gap-2.5 py-1 -mx-1 px-1 rounded transition-colors ${
                            disabled
                              ? "cursor-not-allowed opacity-40"
                              : "cursor-pointer hover:bg-stone-100"
                          }`}
                        >
                          <Checkbox
                            checked={isOn}
                            disabled={disabled}
                            onCheckedChange={() => toggle(u.id)}
                          />
                          <span
                            className={`text-sm leading-snug truncate ${
                              isOn ? "text-stone-900 font-medium" : "text-stone-700"
                            }`}
                          >
                            {u.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {list && list.length < count && (
                    <button
                      type="button"
                      onClick={() => picker.loadMore(country)}
                      data-testid={`compare-country-more-${country.replace(/\s+/g, "-")}`}
                      className="mt-2 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
                    >
                      Show more ({(count - list.length).toLocaleString()} not shown)
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* --- Compare / Clear action bar ---------------------------------- */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 flex-1">
          {selected.length} of {cap} selected
          {atCap && <span className="ml-2 text-stone-400">· deselect one to swap</span>}
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="compare-clear-btn"
            className="inline-flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-800 transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={runCompare}
          disabled={!canCompare}
          data-testid="compare-run-btn"
          className={`inline-flex items-center gap-1.5 text-[12px] font-underground-sc tracking-wider rounded-full px-4 py-2 transition-colors ${
            canCompare
              ? "bg-[#4C1D95] text-white hover:bg-[#3B0764]"
              : "bg-stone-200 text-stone-400 cursor-not-allowed"
          }`}
        >
          Compare
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      {/* NOTE: The APPLY-pillar confirm gate lives on `/shortlist`
          now, not here. This Compare page is a generic side-by-side
          tool — the "final list" a student commits to Apply is the
          shortlist they built by shortlisting courses. */}

      {comparing && chosen.length > 0 && (
        <>
          <ProfessorVerdictCard kind="university" ids={chosen.map((u) => u.id)} />
          <Card
            ref={tableRef}
            data-testid="compare-results-card"
            className="mt-2 border-stone-200 overflow-x-auto"
          >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white w-56">Metric</TableHead>
                {chosen.map((u) => (
                  <TableHead key={u._reqId} className="font-editorial text-base">
                    {u.name}
                    <div className="text-xs text-stone-500 font-underground-sc mt-1 tracking-wider">{u.country}</div>
                    {u.country === "UK" && (
                      <NssTrendSparkline uniId={u.id} />
                    )}
                    {(u.country === "AU" || u.country === "Australia") && (
                      <StudentExperienceTrendSparkline uniId={u.id} country={u.country} />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {UNI_ROWS.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="sticky left-0 bg-white label-uppercase !text-[11px]">{r.label}</TableCell>
                  {chosen.map((u) => (
                    <TableCell key={u._reqId} className="font-mono text-right">
                      {uniCell(r, u[r.key], format)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="sticky left-0 bg-white label-uppercase !text-[11px]">Key warnings</TableCell>
                {chosen.map((u) => (
                  <TableCell key={u._reqId} className="text-sm">
                    <ul className="space-y-1 text-red-900">
                      {(u.warnings || []).slice(0, 3).map((w) => <li key={w}>· {w}</li>)}
                    </ul>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="sticky left-0 bg-white label-uppercase !text-[11px]">Key strengths</TableCell>
                {chosen.map((u) => (
                  <TableCell key={u._reqId} className="text-sm">
                    <ul className="space-y-1 text-emerald-900">
                      {(u.strengths || []).slice(0, 3).map((s) => <li key={s}>· {s}</li>)}
                    </ul>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </Card>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Degrees tab — the new degree-level comparison
// ============================================================================
const money = (n) => n == null ? "—" : `$${Math.round(n).toLocaleString()}`;
const pct = (n) => n == null ? "—" : `${(n * 100).toFixed(1)}%`;


const DEGREE_ROWS = [
  { key: "university", label: "University" },
  { key: "country", label: "Country" },
  { key: "qualification", label: "Qualification" },
  { key: "duration", label: "Duration" },
  { key: "department", label: "Department" },
  { key: "offer_rate", label: "Offer rate", fmt: pct, best: "high" }, // higher = easier to get in
  { key: "typical_a_level", label: "Typical A-Level" },
  { key: "typical_ib", label: "Typical IB" },
  { key: "min_maths", label: "Maths requirement" },
  { key: "ielts_min", label: "IELTS min" },
  { key: "interview", label: "Interview required" },
  { key: "entrance_test", label: "Entrance test" },
  { key: "fee_status", label: "Fee status" },
  { key: "tuition_annual", label: "Tuition / yr" },
  { key: "total_cost", label: "Total degree cost", cmpKey: "total_cost_num", best: "low" },
  // Sourced annual reference for incomplete rows only: never ranked, never multiplied by duration.
  { key: "partial_annual_reference", label: "Partial annual reference", onlyIfAny: true },
  { key: "top_scholarship", label: "Top scholarship" },
  { key: "net_after_schol", label: "Net after top aid", cmpKey: "net_after_schol_num", best: "low" },
  { key: "employment_rate", label: "Employment rate", fmt: pct, best: "high" },
  { key: "median_salary", label: "Median starting salary" },
  { key: "top_employers", label: "Top employers" },
  { key: "psw_years", label: "Post-study work" },
  { key: "stem_designated", label: "STEM-designated" },
  { key: "shortage", label: "Shortage-occupation" },
];

function bestIndex(vals, direction) {
  // vals is an array of raw comparable values (numbers or nulls)
  const numeric = vals.map((v) => (typeof v === "number" && !isNaN(v)) ? v : null);
  if (numeric.every((v) => v == null)) return -1;
  let bestIdx = -1;
  let bestVal = direction === "high" ? -Infinity : Infinity;
  numeric.forEach((v, i) => {
    if (v == null) return;
    if (direction === "high" ? v > bestVal : v < bestVal) {
      bestVal = v;
      bestIdx = i;
    }
  });
  return bestIdx;
}

/**
 * Fee-status row cell. Renders a coloured badge for the status and, when
 * we have one, a small "why" tooltip that cites the engine's reason and
 * links to the official authority page. For logged-out users we show the
 * default (Intl) with a sign-in prompt so they understand why.
 */
function FeeStatusCell({ m, degreeId, loggedOut }) {
  const isHome = m.fee_status === "home";
  const badgeClass = isHome
    ? "bg-emerald-100 text-emerald-800"
    : "bg-stone-100 text-stone-700";
  return (
    <div className="font-mono text-[12px] space-y-1">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          data-testid={`compare-fee-status-badge-${degreeId}`}
          className={`px-2 py-0.5 rounded-full font-semibold text-[11px] uppercase tracking-wider ${badgeClass}`}
        >
          {m.fee_status_label}
        </span>
        {m.fee_status_reason && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid={`compare-fee-status-why-${degreeId}`}
                  aria-label="Why this fee status?"
                  className="inline-flex align-middle text-stone-400 hover:text-[#4C1D95] transition-colors"
                >
                  <HelpCircle className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs text-xs leading-snug font-normal bg-[#0F172A] text-white border-none"
              >
                {m.fee_status_reason}
                {m.fee_status_authority && (
                  <>
                    {" "}
                    <a
                      href={m.fee_status_authority}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-amber-200 hover:text-amber-100"
                    >
                      Confirm with authority
                    </a>
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {m.fee_status_is_fallback && (
        <div className="text-[10px] text-stone-500 italic">Course-level home rate not published — using country average.</div>
      )}
      {loggedOut && (
        <Link to="/profile" className="text-[10px] text-[#4C1D95] hover:underline" data-testid={`compare-fee-status-signin-${degreeId}`}>
          Sign in to personalise
        </Link>
      )}
    </div>
  );
}

function DegreeCompare() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [feeStatusMap, setFeeStatusMap] = useState(null);  // engineKey → inference
  const { format } = useCurrency();

  // Read initial ids from URL. Cap raised from 3 → 6 in iter56 so the
  // Shortlist "Compare all my saved courses" CTA can pass every saved pick.
  const idParams = useMemo(() => {
    return ["d1", "d2", "d3", "d4", "d5", "d6"]
      .map((k) => params.get(k))
      .filter(Boolean);
  }, [params]);

  const typeahead = useDegreeOptions(query, idParams);

  // Pull the signed-in student's fee-status inference once; it drives
  // the Fee-status row + the Tuition / Total / Net recomputation. 401
  // (logged-out) is fine — we just leave the map null and every row
  // falls back to the international rate.
  useEffect(() => {
    api.get("/fee-status/me")
      .then((r) => setFeeStatusMap(r.data?.by_country || null))
      .catch(() => setFeeStatusMap(null));
  }, []);

  // Fetch comparison data when ids change
  useEffect(() => {
    if (!idParams.length) {
      setRows([]);
      return;
    }
    setLoading(true);
    api.get(`/compare/degrees?ids=${idParams.join(",")}`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [idParams.join(",")]);  // eslint-disable-line

  const setIds = (ids) => {
    const next = new URLSearchParams(params);
    ["d1", "d2", "d3", "d4", "d5", "d6"].forEach((k) => next.delete(k));
    ids.slice(0, 6).forEach((id, i) => next.set(`d${i + 1}`, id));
    next.set("tab", "degrees");
    setParams(next, { replace: true });
  };

  const addDegree = (id) => {
    if (idParams.includes(id)) return;
    if (idParams.length >= 6) return;
    setIds([...idParams, id]);
    setQuery("");
  };

  const removeDegree = (id) => setIds(idParams.filter((x) => x !== id));

  const pickableList = typeahead.items.filter((d) => !idParams.includes(d.id));
  const noMatches = typeahead.settled && pickableList.length === 0;

  const metrics = rows.map((r) => degreeMetrics(r, format, feeStatusMap));
  const satisfactionMap = useStudentLovedBulk(rows.map((r) => r.degree.id));

  // Popular subject chips — quick-fill the search box. Not exhaustive;
  // just the eight subjects users hit hardest. Any chip label gets fed
  // into `query`, so the same typeahead handles the result surfacing.
  const SUBJECT_CHIPS = [
    "Computer Science", "Economics", "Engineering", "Business",
    "Law", "Medicine", "Psychology", "Data Science",
  ];

  // Curated example scenarios — labeled explicitly as EXAMPLES so
  // users understand these are launch points, not the entire universe
  // of comparable degrees. Each card carries a short editorial tagline.
  const EXAMPLE_TRIOS = [
    {
      id: "cs-elite",
      tagline: "The CS elite question",
      subtitle: "Oxford CS · MIT EECS · Stanford CS",
      note: "Three top-5 CS programmes — but wildly different price, culture and post-study work rights.",
      ids: [
        "uk-oxford__computer-science__oxford__2027-10",
        "us-mit__eecs__cambridge__2027-09",
        "us-stanford__computer-science__stanford__2027-09",
      ],
    },
    {
      id: "econ-europe",
      tagline: "Economics — three countries",
      subtitle: "Stanford Econ · LMU VWL · PSL Dauphine",
      note: "US Ivy vs Munich's flagship public vs a top Paris grande école. Same subject, ~$300k cost gap.",
      ids: [
        "us-stanford__economics__stanford__2027-09",
        "de-lmu__economics__munich__2027-10",
        "fr-psl__dauphine-economics__paris__2027-09",
      ],
    },
  ];

  const hasSelection = idParams.length > 0;

  return (
    <div>
      {/* Selected chips */}
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {rows.map((r) => (
            <div
              key={r.degree.id}
              data-testid={`selected-degree-${r.degree.id}`}
              className="flex items-center gap-2 bg-[#4C1D95]/10 text-[#4C1D95] border border-[#4C1D95]/30 rounded-full pl-3 pr-1 py-1 text-sm">
              <span className="font-medium">{r.university?.name}</span>
              <span className="text-stone-500">·</span>
              <span>{r.degree.official_title}</span>
              <button
                data-testid={`remove-degree-${r.degree.id}`}
                onClick={() => removeDegree(r.degree.id)}
                aria-label="Remove degree"
                className="ml-1 rounded-full p-1 hover:bg-[#4C1D95]/20">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* --- INTRO: prominent search + subject chips + example cards ---- */}
      {!hasSelection && (
        <div data-testid="degree-compare-intro" className="space-y-8">
          <div>
            <div className="label-uppercase text-stone-500 mb-2">Start here</div>
            <h2 className="font-editorial text-2xl sm:text-3xl leading-tight text-stone-900 max-w-2xl">
              Search any degree we cover, or start from a subject area.
            </h2>
            <p className="mt-2 text-sm text-stone-600 max-w-xl leading-snug">
              You can compare <span className="font-medium">up to 6 degrees</span> from anywhere in our
              catalogue — Oxbridge, Ivy, Grandes Écoles, TU9, Go8, or a directory course from HESA / College
              Scorecard. The examples further down are just launchpads.
            </p>

            <div className="mt-5 relative max-w-2xl">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                data-testid="degree-picker-search"
                placeholder="Try: Oxford CS · MIT EECS · Stanford Economics · TUM Informatics…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 h-12 text-base bg-white border-stone-300"
              />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-underground-sc tracking-wider text-stone-500 mr-1">
                Popular subjects
              </span>
              {SUBJECT_CHIPS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`degree-subject-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setQuery(s)}
                  className="text-xs px-3 py-1 rounded-full border border-stone-200 bg-white
                             hover:border-[#4C1D95] hover:text-[#4C1D95] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>

            {query && pickableList.length > 0 && (
              <div
                data-testid="degree-picker-results"
                className="mt-3 max-w-2xl border border-stone-200 rounded-md bg-white shadow-sm divide-y"
              >
                {pickableList.map((d) => (
                  <button
                    key={d.id}
                    data-testid={`degree-picker-option-${d.id}`}
                    onClick={() => addDegree(d.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-stone-50 text-sm"
                  >
                    <div>
                      <div className="font-medium">{d.official_title}</div>
                      <div className="text-xs text-stone-500 font-mono">
                        {d.university_id} · {d.qualification} · {d.duration_years} yrs
                      </div>
                    </div>
                    <span className="text-xs font-mono text-[#4C1D95]">+ compare</span>
                  </button>
                ))}
              </div>
            )}
            {query && noMatches && (
              <p className="mt-3 text-sm text-stone-500 max-w-2xl" data-testid="degree-picker-no-match">
                {typeahead.error ? "Search is unavailable right now — try again in a moment." : `No matches for "${query}". Try a shorter query, or browse by subject chip.`}
              </p>
            )}
          </div>

          {/* Example comparisons */}
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <div className="label-uppercase text-stone-500">Example comparisons</div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                Click to load · fully editable
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {EXAMPLE_TRIOS.map((ex) => (
                <button
                  key={ex.id}
                  type="button"
                  data-testid={`preset-${ex.id}`}
                  onClick={() => setIds(ex.ids)}
                  className="text-left p-4 sm:p-5 rounded-lg border border-stone-200 bg-white
                             hover:border-[#4C1D95] hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] font-underground-sc tracking-wider bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">
                      Example
                    </span>
                    <span className="label-uppercase text-[#4C1D95]">{ex.tagline}</span>
                  </div>
                  <div className="font-editorial text-lg text-stone-900 mt-1 leading-tight">
                    {ex.subtitle}
                  </div>
                  <p className="mt-2 text-sm text-stone-600 leading-snug">
                    {ex.note}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] group-hover:gap-2 transition-all">
                    Load this comparison
                    <ArrowRight className="h-3 w-3" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- COMPACT PICKER (once at least one degree is selected) ----- */}
      {hasSelection && idParams.length < 6 && (
        <div className="mb-6">
          <div className="relative max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              data-testid="degree-picker-search-compact"
              placeholder="Add another degree…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {query && pickableList.length > 0 && (
            <div className="mt-2 max-w-lg border border-stone-200 rounded-md bg-white shadow-sm divide-y">
              {pickableList.map((d) => (
                <button
                  key={d.id}
                  data-testid={`degree-picker-option-${d.id}`}
                  onClick={() => addDegree(d.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-stone-50 text-sm">
                  <div>
                    <div className="font-medium">{d.official_title}</div>
                    <div className="text-xs text-stone-500 font-mono">
                      {d.university_id} · {d.qualification} · {d.duration_years} yrs
                    </div>
                  </div>
                  <span className="text-xs font-mono text-[#4C1D95]">+ compare</span>
                </button>
              ))}
            </div>
          )}
          <div className="text-xs font-mono text-stone-500 mt-2">
            {idParams.length}/6 selected — add up to {6 - idParams.length} more
          </div>
        </div>
      )}

      {/* Loading indicator intentionally omitted — the /compare/degrees fetch
          typically completes in <500ms and an inline "Loading comparison…"
          line adds visual noise. Empty-state shell stays visible while the
          request is in flight. */}

      {/* Empty state is now the rich `degree-compare-intro` block above.
          When `rows.length === 0` and no ids are queued, the intro
          renders. When ids ARE queued but the fetch hasn't returned,
          we deliberately show nothing — the request is fast (<500ms)
          and a spinner adds visual noise. */}

      {/* Comparison table */}
      {!loading && rows.length > 0 && (
        <>
          <ProfessorVerdictCard kind="degree" ids={rows.map((r) => r.degree.id)} />

          {/* $49 pre-WUA+ Personalised Report — surfaces when the user
              has 2+ degrees on the compare grid. A table shows the
              *what*; the report gives the *so what* (pick + timeline). */}
          {rows.length >= 2 && (
            <div className="my-6">
              <PlusReportCompactStrip
                testId="compare-plus-report-strip"
                hook={`You're comparing ${rows.length} course${rows.length === 1 ? "" : "s"}. A WUA Personalised Report reads the trade-offs for you and picks — 60 seconds, $49 one-off.`}
              />
            </div>
          )}

          <Card className="border-stone-200 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white w-56 z-10">Metric</TableHead>
                {rows.map((r) => (
                  <TableHead key={r.degree.id} className="font-editorial text-base align-top min-w-[220px]">
                    <Link
                      to={`/degree/${r.degree.id}`}
                      className="hover:underline inline-flex items-center gap-1"
                      data-testid={`compare-header-link-${r.degree.id}`}>
                      {r.degree.official_title}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                    <div className="text-xs text-stone-500 font-underground-sc mt-1 tracking-wider">
                      {r.university?.name}
                    </div>
                    <div className="mt-1.5">
                      <StudentLovedBadge
                        stats={satisfactionMap[r.degree.id]}
                        size="xs"
                        testid={`student-loved-compare-${r.degree.id}`}
                      />
                    </div>
                    <CompareChipBar
                      degree={r.degree}
                      testidBase={`compare-${r.degree.id}`}
                    />
                    <div className="mt-1.5">
                      <PlacementsHintChip
                        degree={r.degree}
                        testid={`compare-placements-hint-${r.degree.id}`}
                      />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {DEGREE_ROWS.filter((r) => !r.onlyIfAny || metrics.some((m) => m[r.key] != null)).map((row) => {
                // Compute best-cell if configured
                const cmp = row.cmpKey || (row.fmt === pct ? row.key : null);
                let bestIdx = -1;
                if (row.best && cmp) {
                  const vals = metrics.map((m) => m[cmp]);
                  bestIdx = bestIndex(vals, row.best);
                }
                // Fee-status row has its own tile renderer — status
                // badge, confidence pip and an authority link beat an
                // em-dash when the student isn't logged in.
                if (row.key === "fee_status") {
                  return (
                    <TableRow key={row.key} data-testid="compare-row-fee_status">
                      <TableCell className="sticky left-0 bg-white label-uppercase !text-[11px] z-10">
                        {row.label}
                      </TableCell>
                      {metrics.map((m, i) => (
                        <TableCell
                          key={rows[i].degree.id + "::fee_status"}
                          data-testid={`compare-cell-${rows[i].degree.id}-fee_status`}
                          className="text-sm align-top"
                        >
                          <FeeStatusCell m={m} degreeId={rows[i].degree.id} loggedOut={feeStatusMap === null} />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={row.key} data-testid={`compare-row-${row.key}`}>
                    <TableCell className="sticky left-0 bg-white label-uppercase !text-[11px] z-10">
                      {row.label}
                    </TableCell>
                    {metrics.map((m, i) => {
                      const raw = m[row.key];
                      const display = row.fmt ? row.fmt(raw) : (raw ?? "—");
                      const isBest = i === bestIdx;
                      const reason = display === "—" ? m._reasons?.[row.key] : null;
                      return (
                        <TableCell
                          key={rows[i].degree.id + "::" + row.key}
                          data-testid={`compare-cell-${rows[i].degree.id}-${row.key}`}
                          className={`text-sm align-top ${isBest ? "bg-emerald-50" : ""}`}>
                          <div className={isBest ? "font-mono text-emerald-900 font-semibold" : "font-mono"}>
                            <span>{display}</span>
                            {reason && (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      data-testid={`compare-cell-why-${rows[i].degree.id}-${row.key}`}
                                      aria-label="Why is this blank?"
                                      className="ml-1.5 inline-flex align-middle text-stone-400 hover:text-[#4C1D95] transition-colors"
                                    >
                                      <HelpCircle className="h-3 w-3" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    className="max-w-xs text-xs leading-snug font-normal bg-[#0F172A] text-white border-none"
                                  >
                                    {reason}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {isBest && (
                            <div className="text-[10px] font-underground-sc text-emerald-700 mt-0.5">
                              best
                            </div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {/* Warnings */}
              <TableRow>
                <TableCell className="sticky left-0 bg-white label-uppercase !text-[11px] z-10">
                  Key warnings
                </TableCell>
                {rows.map((r) => (
                  <TableCell key={r.degree.id + "::warn"} className="text-sm align-top">
                    <ul className="space-y-1 text-red-900">
                      {(r.degree.warnings || []).slice(0, 3).map((w, i) => (
                        <li key={i}>
                          <span className="font-mono text-[10px] uppercase tracking-wide text-red-700 mr-1">
                            {w.code}
                          </span>
                          {w.text}
                        </li>
                      ))}
                      {(r.degree.warnings || []).length === 0 && <li className="text-stone-500">—</li>}
                    </ul>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </Card>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Top-level Compare page — tab-driven
// ============================================================================
export default function Compare() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "degrees" || params.get("d1") ? "degrees" : "universities";
  const [tab, setTab] = useState(initialTab);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(params);
    if (v === "degrees") next.set("tab", "degrees");
    else next.delete("tab");
    setParams(next, { replace: true });
  };

  // Detect whether the current tab has an active comparison so the
  // Share/Print buttons only appear when there's something to share.
  const hasActiveComparison =
    (tab === "degrees" && ["d1", "d2", "d3", "d4", "d5", "d6"].some((k) => params.get(k))) ||
    (tab === "universities" && ["u1", "u2", "u3", "u4"].some((k) => params.get(k)));

  const shareLink = async () => {
    try {
      const url = window.location.href;
      if (navigator.share) {
        // Native share sheet on mobile — best UX where available.
        try {
          await navigator.share({ title: "WhichUniversityAdvisor · Comparison", url });
          return;
        } catch { /* user cancelled — fall through to clipboard */ }
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — anyone with this URL sees the same comparison.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  const printPage = () => {
    // Browser handles Save-as-PDF from the print dialog. Uses the
    // print-only stylesheet in index.css to hide the header, tabs,
    // buttons and pickers so the exported page is just the table.
    window.print();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="label-uppercase">Compare</div>
            <Link
              to="/compare/trending"
              data-testid="compare-trending-link"
              className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
            >
              <TrendingUp className="h-3 w-3" />
              trending verdicts
            </Link>
          </div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-2">Side by side, honestly.</h1>
          <p className="mt-3 text-stone-600 max-w-2xl">
            Rankings are too blunt. Compare the specific programme, cost after aid, and outcomes
            you actually care about.
          </p>
        </div>
        {hasActiveComparison && (
          <div className="flex gap-2 print:hidden" data-testid="compare-actions">
            <button
              type="button"
              onClick={shareLink}
              data-testid="compare-share-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-stone-300 bg-white hover:border-[#4C1D95] hover:text-[#4C1D95] text-xs font-underground-sc tracking-wider"
            >
              <Share2 className="h-3.5 w-3.5" />
              Share link
            </button>
            <button
              type="button"
              onClick={printPage}
              data-testid="compare-print-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#4C1D95] text-white hover:bg-[#3B0764] text-xs font-underground-sc tracking-wider"
            >
              <Printer className="h-3.5 w-3.5" />
              Print / PDF
            </button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={changeTab} className="mt-8">
        <TabsList data-testid="compare-tabs" className="grid grid-cols-2 max-w-md print:hidden">
          <TabsTrigger value="universities" data-testid="compare-tab-unis">Universities (up to 4)</TabsTrigger>
          <TabsTrigger value="degrees" data-testid="compare-tab-degrees">Degrees (up to 6)</TabsTrigger>
        </TabsList>
        <TabsContent value="universities" className="mt-6">
          <UniversityCompare />
        </TabsContent>
        <TabsContent value="degrees" className="mt-6">
          <DegreeCompare />
        </TabsContent>
      </Tabs>

      {/* Advisor CTA — the "still can't decide?" flag families reach when
          the side-by-side is honest but doesn't collapse to a single
          winner. Points at WUA+ where a human counsellor can help
          weigh trade-offs the tables can't (fit, gut instinct, family
          dynamics). Kept below the tabs, not floating, so it doesn't
          pressure the comparison itself. */}
      <div
        data-testid="compare-advisor-cta"
        className="mt-16 rounded-2xl border border-[#4C1D95]/25 bg-gradient-to-br from-[#F5F0FF] via-[#EEF2FF] to-white p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
      >
        <div className="shrink-0 h-12 w-12 rounded-full bg-[#4C1D95] text-white flex items-center justify-center">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-uppercase text-[#4C1D95]">Need a second opinion?</div>
          <h3 className="font-editorial text-2xl sm:text-3xl mt-1 leading-tight text-stone-900">
            Still can't make your mind up?
          </h3>
          <p className="mt-2 text-stone-700 leading-relaxed text-[15px] max-w-2xl">
            Speak one to one to a <strong>WUA+ Advisor</strong>. We are here to help — a human counsellor
            can weigh the trade-offs the tables can't quite capture.
          </p>
        </div>
        <Link
          to="/plus"
          data-testid="compare-advisor-cta-link"
          className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4C1D95] text-white text-sm font-medium hover:bg-[#3B0764] transition shadow-sm"
        >
          Explore WUA+
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
